import {BaseElementAsync, MsgLabel, OMsgLabelInit} from "back/commons/basis";
import {MxFormElement, OFormElementInit} from "lib/commons/forms";
import {IReg, REG} from "lib/commons/registry";
import {DOM, JSX} from "lib/commons/xml/dom";
import {Button} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {OUsersGridInit, UsersGrid} from "back/core/widgets/usersGrid";
import {EUserType, JUser, USER} from "lib/core/user";
import {ICoreUniverseEnv} from "lib/core/universe";
import {IPopupable} from "back/commons/widgets/popupable";
import {OUserRefInit, UserRef} from "back/core/widgets/userRef";
import {DOMSH} from "lib/commons/xml/domsh";
import {OUserSelectorInit, UserSelector} from "back/core/dialogs/userSelector";
import {Action} from "lib/commons/actions";


export interface OInputUserInit<GRIDACTCTX> extends OFormElementInit {
	reg: IReg<ICoreUniverseEnv>;
	/** Valeur initiale du champ */
	value?: string;
	/** Sélection des utilisateurs avec confirmation. false par défaut */
	selectWithConfirm?: boolean;
	/** le btn de suppression est masqué. La capacité de suppression est donc gérée en amont */
	hideRemButton?: boolean;
	/** Cardinalité. single par défaut */
	userCard?: 'single' | 'multi';
	/** Message affiché lorsqu'aucune valeur n'est sélectionné */
	emptySelectionMsg?: string;
	errorMsgs?: {
		/** Message d'erreur affiché lorsqu'aucune valeur n'est sélectionné si l'input est requis */
		'empty'?: string,
		/** Message d'erreur affiché en useCard single, avec plusieurs entrées définies */
		'tooMany'?: string,
	}
	/** Init du usersGrid permettant de sélectionner l'utilisateur/groupe */
	usersGridInit?: OUsersGridInit<GRIDACTCTX>,

	/** Init du usersSelector (dans le mode selectWithConfirm=true) */
	usersSelectorInit?: OUserSelectorInit,
}


/**
 * Input de sélection d'un/des utilisateurs
 */
export class InputUserPanel<GRIDACTCTX> extends MxFormElement(BaseElementAsync) {

	reg: IReg<ICoreUniverseEnv>;

	protected _preview: HTMLDivElement;

	protected _msg: MsgLabel;

	protected _editBtn: Button;

	protected _setAccounts: string[] = [];

	protected _setUsers: JUser[] | null;

	protected _forcedIniUsers: JUser[] | null;

	protected _params: OInputUserInit<GRIDACTCTX>;

	protected _usersGrid: UsersGrid<GRIDACTCTX>;

	/** Popup actuellement ouvert */
	protected _openPopup: IPopupable<JUser[]> | null;

	name: string;

	get value(): string[] {
		return this._setAccounts;
	}

	set value(vals: string[]) {
		this._setAccounts = vals || [];
		this._setUsers = undefined;
		this.initializedAsync.then(() => {
			this._refreshUi();
		});
	}

	/**
	 * Cette liste d'utilisateurs correspond à l'état initial utilisé pour les affectations hétérogènes.
	 * Cet état initial peut évolue en cas de suppression, ou d'ajout d'un utilisateur présent dans cette liste
	 * ... mais reste inchangé dans les autres cas (ajout d'un nouvel utlisteur)
	 */
	get forcedIniUsers(): JUser[] {
		return this._forcedIniUsers;
	}

	set forcedIniUsers(vals: JUser[]) {
		this._forcedIniUsers = vals || [];
		this.initializedAsync.then(() => {
			this._refreshUi();
		});
	}

	/** Résout la liste courante des objets user */
	private async getUsersValues(): Promise<JUser[] | null> {
		if (!this._setAccounts || this._setAccounts.length == 0) return;
		if (!this._setUsers) {
			this._setUsers = await this.reg.env.universe.useUsers.getUserSet(this._setAccounts, true);
			this._setUsers.sort(this.sortUsers);
		} else if (this._setUsers.length != this._setAccounts.length) {
			const newAccounts: string[] = [];
			this._setAccounts.forEach((account) => {
				if (!this._setUsers.findIndex((entry) => entry.account === account))
					newAccounts.push(account)
			});
			this._setUsers = this._setUsers.concat(await this.reg.env.universe.useUsers.getUserSet(newAccounts, true));
			this._setUsers.sort(this.sortUsers);
		}
		return this._setUsers;
	}

	formDisabledCallback(disabled: boolean) {
		DOM.setAttrBool(this._preview, "disabled", disabled);
	}

	protected async _initialize(init: OInputUserInit<GRIDACTCTX>): Promise<void> {
		this.reg = this.findReg(init);
		this._params = Object.assign({
			userCard: "single",
		} as OInputUserInit<GRIDACTCTX>, init);

		DOM.setAttrBool(this, "data-card-isSingle", this._params.userCard === 'single');
		DOM.setAttrBool(this, "data-card-isMulti", this._params.userCard === 'multi');

		this._msg = <c-msg hidden/> as MsgLabel;
		const editBox = <div class="inputBox"/> as HTMLDivElement;
		this._preview = editBox.appendChild(<div class="preview"/>) as HTMLDivElement;

		const actsBox = editBox.appendChild(<div class="acts"/>) as HTMLDivElement;
		if (this._params.userCard == 'multi') {
			actsBox.appendChild(<button class="addUsers" onclick={this.onAddUsers} title="Ajouter des utilisateurs"/>)
			this._preview.onclick = this.onAddUsers;
		} else {
			actsBox.appendChild(<button class="selectUser" onclick={this.onSelectUser} title="Sélectionner l'utilisateur"/>);
			this._preview.onclick = this.onSelectUser;
		}

		this._attach(this.localName, init, editBox, this._msg);

		this.addEventListener('keydown', async function (this: InputUserPanel<GRIDACTCTX>, ev: KeyboardEvent) {
			if (!this.disabled && (ev.key === 'Delete' || ev.key === 'Backspace') && this === DOMSH.findDocumentOrShadowRoot(this).activeElement) {
				this.indeterminate = true;
				this.value = null;
				ev.stopPropagation();
				this.dispathChangeEvent();
				return true;
			}
			return false;
		});

		this.tabIndex = 0;
		this._initializeForm(init);
		await this._refreshUi();
	}

	/** Recalcul complet UI */
	private _pendingRefresh: number | undefined;
	private _mustRefresh: boolean;

	protected async _refreshUi() {
		if (this._pendingRefresh) {
			this._mustRefresh = true;
			return;
		} else
			this._mustRefresh = false;

		const currentRefresh = this._pendingRefresh = Date.now();

		try {
			const forcedMsg = "Compte imposé par le contexte. Sans directive explicite, il ne sera ni ajouté, ni supprimé";
			this._preview.innerHTML = '';
			const users = this.value?.length ? await this.getUsersValues() : null;
			if (currentRefresh !== this._pendingRefresh) return;//raceCond
			if (!this.disabled) {
				if ((users && users.length > 0) || (this.forcedIniUsers && this.forcedIniUsers.length) > 0) {
					if (this.forcedIniUsers) {
						for (const u of this.forcedIniUsers) {
							const entry = this._preview.appendChild(<div class="user">
								<UserRef class="forced" title={forcedMsg} î={{reg: this.reg, user: u, withTitle: false} as OUserRefInit}/>
							</div>);
							if (!this._params.hideRemButton) entry.appendChild(<button class="remUser" onclick={this.onRemUser} title="Supprimer"/>);
						}
					}
					if (users) {
						for (const u of users) {
							//Si pb config card=single mais >1 users, on garde le bouton suppr dans tous les cas.
							const entry = this._preview.appendChild(<div class="user">
								<UserRef î={{reg: this.reg, user: u} as OUserRefInit}/>
							</div>);
							if (!this._params.hideRemButton) entry.appendChild(<button class="remUser" onclick={this.onRemUser} title="Supprimer"/>);
						}
					}
				} else
					this._preview.appendChild(<MsgLabel î={{label: ("emptySelectionMsg" in this._params) ? this._params.emptySelectionMsg : "non défini"} as OMsgLabelInit}/>);
			} else {
				//Pas éditable
				if ((users && users.length > 0) || (this.forcedIniUsers && this.forcedIniUsers.length > 0)) {
					if (this.forcedIniUsers)
						for (const u of this.forcedIniUsers) {
							this._preview.appendChild(<UserRef class="forced" title={forcedMsg} î={{reg: this.reg, user: u, withTitle: false} as OUserRefInit}/>);
						}
					if (users)
						for (const u of users) {
							this._preview.appendChild(<UserRef î={{reg: this.reg, user: u} as OUserRefInit}/>);
						}
				} else
					this._preview.appendChild(<MsgLabel î={{label: ("emptySelectionMsg" in this._params) ? this._params.emptySelectionMsg : "non défini"} as OMsgLabelInit}/>);
			}
		} finally {
			if (currentRefresh === this._pendingRefresh)
				this._pendingRefresh = null;
		}

		// Une demande de refresh est arrivée le temps de ce refresh => on relance un refresh
		if (this._mustRefresh) {
			this._mustRefresh = false;
			this._pendingRefresh = null;
			await this._refreshUi();
		} else
			this._refreshValidity();
	}

	/**
	 * Affiche le widget d'ajout de user
	 */
	public addUser() {
		if (this._params.userCard == 'multi')
			this.onAddUsers.call(this);
		else
			this.onSelectUser.call(this);
	}

	/**
	 * Selecteur de user
	 * La méthode "doSearchUsers()" DOIT être appelée explicitement pour rafraichir la liste des users proposés par ce selector
	 * @private
	 */
	private async makeSelector(): Promise<UsersGrid<any>> {
		//{selType: this._params.userCard === "single" ? "mono" : "multi"}
		if (!this._lastSelector) {
			if (this._params.selectWithConfirm) {
				this._lastSelector = new UserSelector().initialize(Object.assign({
					reg: this.reg,
					buttonLabel: this._params.userCard === "single" ? "Sélectionner" : "Ajouter",
					userGrid: Object.assign({
						usersSrv: this.reg.env.universe.useUsers,
						grid: {selType: this._params.userCard === "single" ? "mono" : "multi"},
						filterType: EUserType.user,
						filterTypeInputVisibility: false,
						preserveSelectionOnFilter: true,
					} as OUsersGridInit<GRIDACTCTX>, this._params.usersGridInit),
					selectAndCloseOnDblClick: true
				} as OUserSelectorInit, this._params.usersSelectorInit));
				this._lastSelectorGrid = (this._lastSelector as UserSelector).userGrid;


			} else {
				this._lastSelectorGrid = this._lastSelector = new UsersGrid().initialize(Object.assign({
					usersSrv: this.reg.env.universe.useUsers,
					grid: "mono",
					defaultAction: new Action().setLabel("Sélectionner")
						.setExecute((ctx)=> {
							if (this._openPopup && this._openPopup.opened) this._openPopup.close(this._lastSelectorGrid.getSelectedUsers())
						}),
					filterType: EUserType.user,
					filterTypeInputVisibility: false,
					disableAutoSearchOnInit: true,
				} as OUsersGridInit<GRIDACTCTX>, this._params.usersGridInit)) as UsersGrid<GRIDACTCTX>;
				//await this._lastSelector.initializedAsync;
			}
		} else
			await this._lastSelectorGrid.razFilters();
		return this._lastSelectorGrid;
	}

	private _lastSelector: HTMLElement;
	private _lastSelectorGrid: UsersGrid<any>;

	/**
	 * Sélection d'un *unique* utilisateur
	 * @param ev
	 * @protected
	 */

	private async onSelectUser(this: HTMLButtonElement | InputUserPanel<any>, ev?: Event) {
		ev?.stopImmediatePropagation();
		const me = this instanceof InputUserPanel ? this : DOMSH.findHost<InputUserPanel<any>>(this);
		if (me.disabled) return;
		const prevVal = me._setAccounts;

		const userSelector = await me.makeSelector();
		await userSelector.doSearchUsers();
		userSelector.selectByJUser(me._setUsers || [], true);

		me._openPopup = POPUP.showMenuFromEvent(me._lastSelector, ev || me, me, null, {initWidth: '20em'});
		const users = await me._openPopup.onNextClose() as JUser[];
		me._openPopup = null;

		if (users && users.length > 0) {
			me._setAccounts = [];
			me._forcedIniUsers = [];
			users.forEach(value => me._setAccounts.push(value.account));
			/* Vérification de l'état dirty par comparaison du JSON */
			if (!InputUserPanel.isAccountsListEquals(prevVal, me._setAccounts)) {
				me._setUsers = users;
				me.focus();
				await me._refreshUi();
				me.dispathChangeEvent();
			}
		}
	}

	/**
	 * Ajout d'un utilisateur
	 * @param ev
	 * @private
	 */
	private async onAddUsers(this: HTMLButtonElement | InputUserPanel<any>, ev?: Event) {
		ev?.stopImmediatePropagation();
		const me = this instanceof InputUserPanel ? this : DOMSH.findHost<InputUserPanel<any>>(this);
		if (me.disabled) return;

		const userSelector = await me.makeSelector();
		if (me._setAccounts.length > 0) userSelector.setContextualUserFilter((user) => me._setAccounts.indexOf(user.account) == -1);
		await userSelector.doSearchUsers();

		me._openPopup = POPUP.showMenuFromEvent<JUser[]>(me._lastSelector, ev || me, me, null, {initWidth: '20em'});
		const users = await me._openPopup.onNextClose();
		me._openPopup = null;

		if (users && users.length > 0) {
			if (!me._setUsers)
				me._setUsers = [];
			users.forEach(value => {
				if (me._forcedIniUsers) {
					const remIdx = me._forcedIniUsers.findIndex((entry) => entry.account === value.account);
					if (remIdx > -1) me._forcedIniUsers.splice(remIdx, 1);
				}
				me._setAccounts.push(value.account);
				me._setUsers.push(value);
			});
			me._setUsers.sort(me.sortUsers);
			me.focus();
			await me._refreshUi();
			me.dispathChangeEvent();
		}
	}

	/**
	 * Suppression d'un utilisateur
	 * @param ev
	 */
	private async onRemUser(this: HTMLButtonElement, ev?: Event) {
		ev?.stopImmediatePropagation();
		const me = DOMSH.findHost<InputUserPanel<any>>(this);
		if (me.disabled) return;
		const userRef = this.previousElementSibling as UserRef;
		if (userRef.classList?.contains('forced')) {
			const remIdx = me._forcedIniUsers.findIndex((entry) => entry.account === userRef.nickOrAccount);
			if (remIdx > -1) me._forcedIniUsers.splice(remIdx, 1);
		} else {
			const remAccIdx = me._setAccounts.findIndex((account) => account === userRef.nickOrAccount);
			me._setAccounts.splice(remAccIdx, 1);
			const remUsersIdx = me._setUsers?.findIndex((user) => user.account === userRef.nickOrAccount);
			if (remUsersIdx > -1) me._setUsers.splice(remUsersIdx, 1);
		}
		me.focus();
		await me._refreshUi();
		me.dispathChangeEvent();
	}

	protected _refreshValidity() {
		if (this.required && (!this._setUsers || this._setUsers.length == 0))
			this.setValidity({valueMissing: true}, this._params.errorMsgs?.empty || "Veuillez renseigner ce champ.");
		else if (this._params.userCard == 'single' && this._setUsers && this._setUsers.length > 1)
			this.setValidity({valueMissing: true}, this._params.errorMsgs?.tooMany || "Veuillez saisir une unique valeur.");
		else this.setValidity({});
	}

	/** */
	private dispathChangeEvent(): void {
		this.dispatchEvent(new CustomEvent("input", {bubbles: true, cancelable: false}));
		this.dispatchEvent(new CustomEvent("change", {bubbles: true, cancelable: false}));
	}

	/** Trie d'une liste d'objets users */
	private sortUsers(u1: JUser, u2: JUser): number {
		return USER.getPrimaryName(u1).localeCompare(USER.getPrimaryName(u2));
	}

	/** Compare deux listes non ordonnées de users */
	static isAccountsListEquals(l1: string[], l2: string[]): boolean {
		if (!l1 || !l2) return false;
		if (l1.length !== l2.length) return false;
		for (const u of l1) if (l2.indexOf(u) == -1) return false;
		return true;
	}

}

REG.reg.registerSkin("c-input-users-panel", 1, /* language=CSS */ `
	:host {
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
	}

	:host(:focus) {
		outline: var(--focus-outline);
	}

	:host(:not([disabled])) {
		cursor: pointer;
	}

	.inputBox {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-wrap: wrap;
	}

	.inputBox {
		background-color: var(--form-bgcolor);
		border: 1px solid var(--border-color);
	}

	:host([disabled]) .inputBox {
		background-color: unset;
		border: unset;
	}

	.preview {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-wrap: wrap;
	}

	.acts {
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	:host([data-card-isMulti]) .acts {
		border-inline-start: 1px solid var(--border-color);
		margin-block-start: 2px;
		margin-block-end: 2px;
		margin-inline-start: 2px;
	}

	.user {
		display: flex;
		min-height: 0;
		min-width: 0;
		padding: .1em;
	}

	:host([data-card-isSingle]) .user {
		cursor: pointer;
		user-select: none;
		flex: 1;
	}

	:host(:not([data-card-isSingle])) .user {
		cursor: default;
		margin: .2em;
		border: 1px solid var(--border-color);
		border-radius: .4em;
		background-color: var(--alt2-bgcolor);
	}

	/*
	:host([data-card-isSingle]) > :only-of-type > .remUser{
		display: none;
	}
	*/

	c-userref {
		color: var(--edit-color);
	}

	c-userref.forced {
		opacity: .6;
	}

	:host([data-card-isSingle]) c-userref {
		flex: 1;
	}

	button {
		border: none;
		cursor: pointer;
		width: var(--icon-size);
		height: var(--icon-size);
		align-self: center;
		font-size: inherit;
	}

	:host([disabled]) button {
		display: none;
	}

	:host(:not([readonly])) button:hover {
		filter: var(--hover-filter);
	}

	:host([readonly]) button {
		display: none;
	}

	.remUser {
		align-self: flex-start;
		background: transparent no-repeat url("/@skin@/commons/icons/delete.svg") top;
		background-size: 65%;
	}

  :host([data-card-isSingle]) .remUser {
	  background-position: center;
  }

  .addUsers {
	  background: transparent no-repeat url("/@skin@/commons/icons/add.svg") center;
	  background-size: 1em 1em;
  }

  .selectUser {
	  background: transparent no-repeat var(--dropdown-url) center;
	  background-size: 1em 1em;
  }

  c-msg {
	  justify-content: flex-start;
	  font-style: italic;
  }

`);

customElements.define('c-input-users-panel', InputUserPanel);